/**
 * SaleDashboardContainer
 * --
 */
import React, { useEffect, useState } from 'react';
import MessageAlert from 'utils';
import moment from 'moment';
import { useParams } from 'react-router-dom';
import SaleDashboardPresenter from './SaleDashboardPresenter';
import { StatisticsApi, OrderApi } from 'api';

const MOMENT_FORMAT = 'YYYY-MM-DD';
const endDate = moment().format(MOMENT_FORMAT);
const startDate = moment().add(-30, 'days').format(MOMENT_FORMAT);

/**----------------------------------------
 * [Component] 매출관리 > 대시보드 - 컨테이너
 * ----------------------------------------
 */
const SaleDashboardContainer = ({ defaultShopId, permission, summaryMode }) => {
  /* ===== Init ===== */
  // 오늘 날짜의 시작 (00:00:00)과 끝 (23:59:59) 타임스탬프 계산
  const { type } = useParams();

  /* ===== Mutate ===== */

  /* ===== VARIABLE ===== */

  /* ===== ROUTE ===== */
  /* ===== PROPS ===== */
  /* ===== STATE ===== */

  /* 주문데이터 */
  const [statisticsType, _setStatisticsType] = useState(type ?? 'daily');
  const [isLoading, setIsLoading] = useState(false);
  const [currentShop, setCurrentShop] = useState(defaultShopId ?? null);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [data, setData] = useState(null); // 차트 raw 데이터
  const [dateRange, setDateRange] = useState({
    startDate,
    endDate,
  });
  const [currentRow, setCurrentRow] = useState(null);
  const [rawData, setRawData] = useState([]);
  const [finalDiscount, setFinalDiscount] = useState(0);
  const [menuCoupon, setMenuCoupon] = useState([]);

  /* ===== FUNCTION ===== */
  /**
   * 검색
   * --
   */
  const handleSearch = (value) => {
    setDateRange(value);
  };

  /**
   * 데이터 조회
   * --
   */
  const getFinalDiscount = async () => {
    try {
      const { status, message, data } = await StatisticsApi.getStatistics(
        'discount',
        {
          ...dateRange,
          shopId: currentShop,
        }
      );
      if (status !== 200) throw { message };
      // console.log(data);
      setFinalDiscount(data[0].final_discount);
    } catch (e) {
      MessageAlert.error(e.message);
    }
  };

  /**
   * 데이터 조회
   * --
   */
  const getMenuCouponSales = async () => {
    try {
      const { status, message, data } = await StatisticsApi.getStatistics(
        'menu_coupon',
        {
          ...dateRange,
          shopId: currentShop,
        }
      );
      if (status !== 200) throw { message };
      setMenuCoupon(data);
    } catch (e) {
      MessageAlert.error(e.message);
    }
  };
  /**
   * 데이터 조회
   * --
   */
  const getStatistics = async () => {
    setIsLoading(true);
    try {
      const { status, message, data } = await StatisticsApi.getStatistics(
        statisticsType,
        {
          // startDate: `${endDate}`,
          // endDate: `${today}`,
          ...dateRange,
          shopId: currentShop,
        }
      );
      if (status !== 200) throw { message };

      setTimeout(() => {
        setData(data);
        setIsLoading(false);
      }, 1350);
    } catch (e) {
      setIsLoading(false);
      MessageAlert.error(e.message);
    }
  };

  /**
   * 한 데이터 선택 시
   * --
   */
  const handleSelectRow = (row) => {
    setCurrentRow(row);
  };

  /* ===== HOOKS ===== */

  /**
   * 차트데이터 조회
   * --
   */
  useEffect(() => {
    currentShop && getStatistics();
    currentShop && getFinalDiscount();
    currentShop &&
      (statisticsType === 'menu' || statisticsType === 'menu_category') &&
      getMenuCouponSales();
  }, [currentShop, statisticsType, dateRange]);

  /**
   * [currentRow데이터 변경 시 실행] 상세 데이터 조회
   * --
   */
  useEffect(() => {
    const getStatisticsDetail = async () => {
      setIsLoading(true);
      try {
        let result;

        // 메뉴옵션별 상세조회
        if (
          currentRow?.STATISTIC_TYPE &&
          currentRow?.STATISTIC_TYPE === 'OPTION'
        ) {
          result = await StatisticsApi.getStatistics('option', {
            ...dateRange,
            shopId: currentShop,
            menu: currentRow.menu_name,
          });
        }
        // 일자별 상세조회
        else {
          result = await OrderApi.getOrders({
            shop_information_id: currentShop,
            start_date: moment(currentRow.date).startOf('day').unix(),
            end_date: moment(currentRow.date).endOf('day').unix(),
          });
        }

        const { status, message, data } = result;
        if (status !== 200) throw { message };

        setTimeout(() => {
          setRawData(data);
          setIsLoading(false);
        }, 1250);
      } catch (e) {
        setIsLoading(false);
        MessageAlert.error(e.message);
      }
    };
    currentRow && getStatisticsDetail();
  }, [currentRow]);

  /* ===== RENDER ===== */
  return (
    <>
      <SaleDashboardPresenter
        {...{
          permission,
          statisticsType,
          data,
          isLoading,
          currentShop,
          searchKeyword,
          setCurrentShop,
          setSearchKeyword,
          startDate,
          endDate,
          currentRow,
          rawData,
          dateRange,
          onSearch: handleSearch,
          onSelect: handleSelectRow,
          finalDiscount,
          menuCoupon,
          summaryMode,
        }}
      />
    </>
  );
};

/* DF */
SaleDashboardContainer.defaultProps = {
  defaultShopId: null,
  permission: 'ADMIN',
  summaryMode: false,
};

export default SaleDashboardContainer;
